// ---------------------------------------------------------------------------
// CKEditor 5 on front-end forms.
//
// The editor itself is not bundled here — it's 1.8 MB and would land on every
// page. modules/alphabet/web/assets/ckeditor/CkeditorLibAsset publishes the
// stock UMD build that ships with craftcms/ckeditor, and templates that want an
// editor register that bundle and mark their textarea with `data-ckeditor`.
//
// The textarea stays in the DOM and stays the thing that submits, so the form
// still works if this never runs.
// ---------------------------------------------------------------------------

// Matches the toolbar configured on the `forumText` field in the control panel,
// so the front end and the CP offer the same formatting.
const TOOLBAR = ['bold', 'italic', 'link', '|', 'bulletedList', 'numberedList'];

async function createEditor(textarea) {
  const { ClassicEditor, Essentials, Paragraph, Bold, Italic, Link, List, Autoformat, Undo } = window.CKEDITOR;

  const editor = await ClassicEditor.create(textarea, {
    // Same value Craft's own CP build uses.
    licenseKey: 'GPL',
    plugins: [Essentials, Paragraph, Bold, Italic, Link, List, Autoformat, Undo],
    toolbar: TOOLBAR,
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
    },
    placeholder: textarea.getAttribute('placeholder') || '',
  });

  // A reply box wants to be shorter than a new-thread box; the stylesheet's
  // default applies when the attribute is absent.
  const minHeight = parseInt(textarea.getAttribute('data-ckeditor-min-height') || '', 10);
  if (minHeight) {
    editor.ui.view.element.style.setProperty('--ck-editable-min-height', `${minHeight}px`);
  }

  // The editable replaces the textarea visually, so it has to carry the
  // textarea's name, hint and error state too — otherwise a screen reader
  // hears CKEditor's generic "Rich Text Editor" with no label (WCAG 1.3.1,
  // 4.1.2). The <label for> stays pointed at the hidden textarea.
  const label = textarea.id ? document.querySelector(`label[for="${CSS.escape(textarea.id)}"]`) : null;
  const labelText = (label?.textContent || textarea.getAttribute('aria-label') || '').replace(/\s*\*\s*$/, '').trim();
  const describedBy = textarea.getAttribute('aria-describedby');
  const invalid = textarea.getAttribute('aria-invalid');

  editor.editing.view.change((writer) => {
    const root = editor.editing.view.document.getRoot();
    if (labelText) writer.setAttribute('aria-label', labelText, root);
    if (describedBy) writer.setAttribute('aria-describedby', describedBy, root);
    if (invalid === 'true') writer.setAttribute('aria-invalid', 'true', root);
    if (textarea.required) writer.setAttribute('aria-required', 'true', root);
  });

  // Clicking the visible label should land in the editor, as it would have in
  // the textarea.
  label?.addEventListener('click', (event) => {
    event.preventDefault();
    editor.editing.view.focus();
  });

  // ClassicEditor.create() on a <textarea> keeps it in the form and syncs on
  // submit, but not on every keystroke — sync eagerly so anything else reading
  // the field (validation, an unload warning) sees current content.
  editor.model.document.on('change:data', () => {
    textarea.value = editor.getData();
  });

  return editor;
}

export default function initCkeditors() {
  const textareas = document.querySelectorAll('textarea[data-ckeditor]');

  if (!textareas.length) return;

  if (!window.CKEDITOR) {
    console.warn('[alphabet] data-ckeditor found but CKEDITOR is missing — did the template register CkeditorLibAsset?');
    return;
  }

  textareas.forEach((textarea) => {
    createEditor(textarea).catch((error) => {
      // Leave the plain textarea in place rather than breaking the form.
      console.error('[alphabet] CKEditor failed to initialise', error);
    });
  });
}
